import { useEffect, useState } from "react";
// import http from "./common/NGhttp";
import { Col, Container, Row, Tab, Tabs, Button } from "react-bootstrap";
import GetIconForSection from "./ui-components/ng-icons/IconUtilities";
import { CheckMarkIcon, CopyIcon, EditIcon, PlusIcon, TrashIcon } from "@sx-icons";
import { ng } from "@common/ngds";
//import NGBeam from "../store/NGBeam";
import { useTranslation } from "react-i18next";
import {
  selectAllMaterials,
  selectAllSections,
  selectSectionFamilies,
  useMaterials,
} from "../features/materialsSections/materialSectionSlice";
import { useAppSelector } from "../app/hooks";
import { MaterialSectionDetail } from "./MaterialSectionDetail";
import { useProject } from "../features/projects/projectSlice";
import { MaterialSettings } from "./MaterialSettings";
import { openModal, selectModalById } from "../features/ui/uiSlice";
import { useDispatch, useSelector } from "react-redux";
import { SectionCreationModal } from "./modals/SectionCreationModal";
import { ContextMenu } from "@common/components/ui-components/SXContextMenu";
import { PopUpModal } from "./modals/common/PopUp";
import { AlertMessage } from "../helpers/AlertMessage";
import context from "react-bootstrap/esm/AccordionContext";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";

// --- Main component ---
interface ProjectInputSectionExplorerProps {
  activeKeys?: string[];
  setToggleSection?: (sec: boolean) => void;
  setWindowIsInfocus?: (value: boolean) => void;
  setToggleMaterialProps?: (value: boolean) => void;
  setToggleAutoDesign?: (value: boolean) => void;
  setDefaultTab?: (value: string | null) => void;
  project?: ng.project.Data;
  reinfCalcError?: { caption: string; text: string } | null;
  handleDoubleClick?: any;
  vpBeam?: null; //NGBeam | null;
}

interface SectionItem {
  family: number;
  guid: string;
  name: string;
  ownertype: number;
  type: number;
}

type MaterialGroup = "Concrete" | "Steel" | "Timber";

interface MaterialItem {
  guid?: string | null;
  name?: string | null;
  product?: number | string | null;
}

export const ProjectInputSectionExplorer = (props: ProjectInputSectionExplorerProps) => {
  //NOTE! Only one material and one section for beam as for now
  const sectionListMaxHeight = 350;

  // --- Contexts ---
  //const states = useContext(StatesContext);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    getSections,
    getSection,
    getMaterial,
    deleteSection,
    setConcreteMaterialForProject,
    setSteelMaterialForProject,
    setTimberMaterialForProject,
  } = useMaterials();
  const [family, setFamily] = useState<string | null>(null);
  const [currentProjectMaterial, setCurrentProjectMaterial] = useState<MaterialGroup>("Concrete");
  const [modalState, setModalState] = useState<"create" | "edit">("create");
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<MaterialGroup>("Concrete");
  const [isContextClickedIndex, setIsContextClickedIndex] = useState<number | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuOptions, setContextMenuOptions] = useState<{ label: string; action: () => void }[]>([]);
  const allSections = useAppSelector(selectAllSections);
  const allMaterials = useAppSelector(selectAllMaterials);
  const pp = useProject();
  const {
    project,
    getProject,
    analyse,
    setProjectMaterialGuid,
    setProjectSectionGuid,
    delSelfWeights,
    addSelfWeights,
  } = pp;
  const sectionFamilies = useAppSelector(selectSectionFamilies);
  const isCreationModalOpen = useSelector(selectModalById("SectionCreationModal"));
  const [deletePopup, setDeletePopup] = useState<{ show: boolean; sectionGuid: string | null }>({
    show: false,
    sectionGuid: null,
  });
  // Map group to its sections key (allSections uses getMtrlDBTemplate keys)
  const sectionsCategoryKey: Record<MaterialGroup, string> = {
    Concrete: "Concrete",
    Steel: "Steel-std",
    Timber: "Timber",
  };

  const currentSections: any = (allSections as any)[sectionsCategoryKey[selectedGroup]] ?? {};

  // For timber, filter materials to those compatible with the selected section family.
  // Product type constants from ng.material.timber.ProductType (numeric enum values).
  const TIMBER_PRODUCT_REGULAR = 1; // PRODUCT_TYPE_REGULAR — sawn / dressed lumber
  const TIMBER_PRODUCT_GL = 2; // PRODUCT_TYPE_GL      — glue laminated
  const TIMBER_PRODUCT_KERTO = 7; // PRODUCT_TYPE_KERTO   — kerto
  const timberFamilyProductTypes: Record<string, number[]> = {
    Sawn: [TIMBER_PRODUCT_REGULAR],
    Dressed: [TIMBER_PRODUCT_REGULAR],
    Glulam: [TIMBER_PRODUCT_GL],
    Kerto: [TIMBER_PRODUCT_KERTO],
  };
  const isTimberFamily = (value: string | null): value is keyof typeof timberFamilyProductTypes =>
    value != null && value in timberFamilyProductTypes;
  const materialsByGroup = allMaterials as Partial<Record<MaterialGroup, MaterialItem[]>>;
  const getMaterialsForGroup = (group: MaterialGroup, nextFamily: string | null): MaterialItem[] => {
    const groupMaterials = materialsByGroup[group] ?? [];
    return group === "Timber" && isTimberFamily(nextFamily)
      ? groupMaterials.filter((mat) => timberFamilyProductTypes[nextFamily].includes(Number(mat.product)))
      : groupMaterials;
  };
  const currentMaterials = getMaterialsForGroup(selectedGroup, family);

  // Determine which group a saved section guid belongs to
  const getGroupForSectionGuid = (guid: string): MaterialGroup => {
    const groups: Array<[MaterialGroup, string]> = [
      ["Concrete", "Concrete"],
      ["Steel", "Steel-std"],
      ["Timber", "Timber"],
    ];
    for (const [group, key] of groups) {
      const catSections = (allSections as any)[key] ?? {};
      const allInCategory: any[] = Object.values(catSections).flat();
      if (allInCategory.some((s: any) => s.guid === guid)) return group;
    }
    return "Concrete";
  };

  const contextMenuOptionsUserDefined = (guid: string, name: string) => [
    {
      label: t("edit_.label"),
      icon: <EditIcon color="#b5b5b5" />,
      action: async () => {
        setModalState("edit");
        setSelectedSection(guid);
        dispatch(openModal("SectionCreationModal"));
      },
    },
    {
      label: t("duplicate_.label"),
      icon: <CopyIcon color="#b5b5b5" />,
      action: async () => {
        setModalState("create");
        setSelectedSection(guid);
        dispatch(openModal("SectionCreationModal"));
      },
    },
    {
      label: t("remove_.label"),
      icon: <TrashIcon color="#b5b5b5" />,
      action: async () => {
        let start = project!.data.data!.input!.structures[0]!.elements[0]!.beam!.segments[0]!.layer!.start || null;

        let end = project!.data.data!.input!.structures[0]!.elements[0]!.beam!.segments[0]!.layer!.end || null;

        if (start == null || guid == start.secGuid || end == null || guid == end.secGuid) {
          AlertMessage("error_", t("error_label"), "", t("MSG_section_is_referenced_cannot_delete_.label"));
          return;
        }

        setDeletePopup({ show: true, sectionGuid: guid });
      },
    },
  ];

  const contextMenuOptionsDefault = (guid: string, name: string) => [
    {
      label: t("duplicate_.label"),
      icon: <CopyIcon />,
      action: () => {
        console.log("Duplicate clicked for section: ", guid, name);
        setSelectedSection(guid);

        setModalState("create");
        dispatch(openModal("SectionCreationModal"));
      },
    },
  ];

  const handleContextClick = (
    guid: string,
    name: string,
    userDefined: boolean,
    index: number,
    event: React.MouseEvent,
    group: string,
  ) => {
    if (selectedGroup == "Steel") return; // Prevent default right-click behavior
    // Set the index of the context-clicked section for styling purposes
    setIsContextClickedIndex(index);
    // Set the menu position to be near the cursor
    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY,
    });
    // setContextMenuGuid(guid); // the guid from selected section
    // Set the context menu options based on whether the section is user-defined or not
    if (group !== "Steel-std") {
      const options = userDefined ? contextMenuOptionsUserDefined(guid, name) : contextMenuOptionsDefault(guid, name);
      setContextMenuOptions(options);
      // Show the context menu
      setShowContextMenu(true);
    }
  };

  useEffect(() => {
    if (
      project &&
      project.data &&
      project.data.data &&
      project?.data?.data?.input?.structures[0]?.elements[0].beam?.segments[0].layer?.start
    ) {
      let secguid = project?.data.data?.input?.structures[0].elements[0].beam?.segments[0].layer.start.secGuid;
      setSelectedSection(secguid ?? "");
      let matguid = project?.data.data?.input?.structures[0].elements[0].beam?.mtrlGuid;

      if (family == null && sectionFamilies.hasOwnProperty(secguid ?? "")) {
        setFamily(sectionFamilies[secguid ?? ""] ?? null);
      }
      setSelectedMaterial(matguid ?? "");
      if (secguid) {
        let secgroup = getGroupForSectionGuid(secguid);
        setSelectedGroup(secgroup);
        setCurrentProjectMaterial(secgroup);
      }
    }
  }, [project]);

  useEffect(() => {
    if (
      project &&
      project.data &&
      project.data.data &&
      project?.data?.data?.input?.structures[0]?.elements[0].beam?.segments[0].layer?.start
    ) {
      let secguid = project?.data.data?.input?.structures[0].elements[0].beam?.segments[0].layer.start.secGuid;

      //  setFamily(sectionFamilies[secguid ?? ""] ?? null);
      if (secguid) {
        let group = getGroupForSectionGuid(secguid);
        //   setCurrentProjectMaterial(group);
        //  setSelectedGroup(group);
      }
      //
    }
  }, [sectionFamilies]);

  useEffect(() => {
    if (
      project &&
      project.data &&
      project.data.data &&
      project?.data?.data?.input?.structures[0]?.elements[0].beam?.segments[0].layer?.start
    ) {
      //  let secguid = project?.data.data?.input?.structures[0].elements[0].beam?.segments[0].layer.start.secGuid;
      // setSelectedSection(secguid ?? "");
    }
  }, [allSections]);

  async function _updateMaterialSpecificData(proj: ng.frontend.column.IProject, oldMtrl: string, newMtrl: string) {
    // Check if the material type has changed, if so we need to update the material specific data.
    if (oldMtrl && newMtrl) {
      if (oldMtrl !== newMtrl) {
        try {
          let rawProj = {};

          switch (newMtrl) {
            case "Concrete":
              rawProj = await setConcreteMaterialForProject(proj);
              break;

            case "Steel":
              rawProj = await setSteelMaterialForProject(proj);

              break;

            case "Timber":
              rawProj = await setTimberMaterialForProject(proj);
              break;

            default:
              console.log("no data set!");
              break;
          }

          let newProj = ng.frontend.column.Project.fromObject(rawProj);

          return newProj;
        } catch (error) {
          console.log("Error in getting material specific data!", error);
        }
      }
    } else {
      console.log("Undefined materials!");
    }

    return proj;
  }

  //----------------------------------------------------
  return (
    <Container id="sx-table-container">
      <Row align="end" style={{ paddingBottom: 10, paddingTop: 10 }}>
        <Col lg="6"></Col>
        <Col></Col>

        <Col>
          <Button
            id="section_use"
            className="secondary-btn"
            onClick={async (e) => {
              try {
                if (selectedSection) {
                  let secgroup = getGroupForSectionGuid(selectedSection);
                  setSelectedGroup(secgroup);
                  if (currentProjectMaterial !== secgroup) {
                    if (project && project.data) {
                      let proj = await _updateMaterialSpecificData(
                        ng.frontend.column.Project.fromObject(project.data),
                        currentProjectMaterial,
                        secgroup,
                      );
                      if (project && proj && project.data) {
                        let projectpy = ng.frontend.column.Project.toObject(proj);
                        let newproj = structuredClone(project);
                        newproj.data = projectpy as ng.frontend.column.Project_json;
                        pp.setProject(newproj);
                      }
                    }
                  }

                  if (selectedSection) {
                    if (selectedMaterial) {
                      // Must finish before setProjectSectionGuid: it fetches the material and
                      // upserts it into mtrlDb, while setProjectSectionGuid fires updateGammaApi,
                      // which snapshots the project and then replaces project.data.data wholesale.
                      // Without awaiting, the late mtrlDb add races (and loses) against that
                      // overwrite, so the new material never lands in mtrlDb.
                      await setProjectMaterialGuid(selectedMaterial);
                    }
                    await setProjectSectionGuid(selectedSection);

                    await pp.generateLoadCombs();
                  }
                }
              } catch (x) {
                report_exception(t, x, t("button_set_.label"));
              }
            }}
          >
            {t("button_set_.label")} <CheckMarkIcon size={14} />
          </Button>
        </Col>
      </Row>

      <Row style={{ alignItems: "center", paddingTop: 10 }}>
        <Col className="ps-0 px-0">
          <Container className="sx-table-style-for-custom-table px-0">
            <Tabs defaultActiveKey="section" id="sectionexplorer-tabs" className="simplex-custom-tabs p-0">
              <Tab eventKey="section" title={t("select_.label")}>
                <Container className="sectionexplorer" style={{ paddingBottom: 10 }}>
                  <Row className="ngtable" style={{ borderRadius: 5 }}>
                    {/** GROUP */}
                    <Col xs={3}>
                      <Row style={{ marginTop: 10 }}>
                        <Col className="ps-0 px-0">
                          <div className="sx-list-header">{t("group_.label")}</div>
                        </Col>
                      </Row>
                      <Row className="table-body align-items-center">
                        <Col className="sx-list px-0">
                          <ul
                            className="pointer sx-list-box sx-list-box--first"
                            style={{ height: sectionListMaxHeight, overflow: "auto" }}
                          >
                            {(["Concrete", "Steel", "Timber"] as const).map((group) => (
                              <li
                                key={group}
                                className={selectedGroup === group ? "active" : ""}
                                id={`${group.toLowerCase()}-btn`}
                                onClick={() => {
                                  const newGroupSections: any = (allSections as any)[sectionsCategoryKey[group]] ?? {};
                                  const firstFamily = Object.keys(newGroupSections)[0] ?? null;

                                  const projectSecGuid =
                                    project?.data?.data?.input?.structures[0]?.elements[0]?.beam?.segments[0]?.layer
                                      ?.start?.secGuid ?? null;
                                  const allInNewGroup: any[] = (Object.values(newGroupSections) as any[][]).flat();
                                  const projectSecInNewGroup =
                                    projectSecGuid != null && allInNewGroup.some((s: any) => s.guid === projectSecGuid);
                                  const nextFamily = projectSecInNewGroup
                                    ? (sectionFamilies[projectSecGuid!] ?? firstFamily)
                                    : firstFamily;

                                  setSelectedGroup(group);
                                  setFamily(nextFamily);
                                  setSelectedSection(projectSecInNewGroup ? projectSecGuid : null);
                                  setSelectedMaterial(getMaterialsForGroup(group, nextFamily)[0]?.guid ?? null);
                                }}
                              >
                                {t(`${group.toLowerCase()}_.label`)}
                              </li>
                            ))}
                          </ul>
                        </Col>
                      </Row>
                    </Col>
                    {/** TYPE */}
                    <Col>
                      <Row style={{ marginTop: 10 }}>
                        <Col className="ps-0 px-0">
                          <div className="sx-list-header">{t("type_.label")}</div>
                        </Col>
                      </Row>
                      <Row className="table-body align-items-center">
                        <Col className="sx-list px-0">
                          <ul
                            className="sx-list-box sx-list-box--second"
                            style={{ height: sectionListMaxHeight, overflow: "auto" }}
                          >
                            {currentSections &&
                              Object.keys(currentSections).map((propKey, index) => {
                                return (
                                  <li
                                    className={propKey === family ? "active" : ""}
                                    id={propKey + "_btn"}
                                    onClick={(ev) => {
                                      setFamily(propKey);
                                    }}
                                    key={index + "_" + propKey}
                                  >
                                    <GetIconForSection sectionName={propKey} />
                                    <span>
                                      {" "}
                                      {"\u00A0"} {propKey}
                                    </span>
                                  </li>
                                );
                              })}
                          </ul>
                        </Col>
                      </Row>
                    </Col>
                    {/** SIZE */}
                    <Col>
                      <Row style={{ marginTop: 10 }}>
                        <Col className="ps-0 px-0">
                          <div className="sx-list-header">{t("size_.label")}</div>
                        </Col>
                        <Col xs={4}>
                          {["Concrete", "Timber"].includes(selectedGroup) && (
                            <span
                              className="pointer"
                              id={"addSection"}
                              data-testid="addSection"
                              onClick={() => {
                                /*_updateMaterialSpecificData(
                                  project!.data.data!.input!.,
                                  project!.data.data!.input!.structures[0]!.elements[0]!.beam!.mtrl!,
                                  null,
                                );
                                */
                                setModalState("create");
                                dispatch(openModal("SectionCreationModal"));
                              }}
                            >
                              <PlusIcon size={14} />
                            </span>
                          )}
                        </Col>
                      </Row>
                      <Row className="table-body align-items-center ">
                        <Col className="sx-list px-0">
                          <ul
                            className="sx-list-box sx-list-box--third"
                            style={{ height: sectionListMaxHeight, overflow: "auto" }}
                            id="section-list"
                          >
                            {family &&
                              currentSections.hasOwnProperty(family) &&
                              Object.values(currentSections[family]).map((val: any, index) => {
                                let userDefined = val.ownertype === 2;
                                return (
                                  <li
                                    key={val.guid}
                                    className={val.guid === selectedSection ? "active" : ""}
                                    onClick={() => {
                                      setSelectedSection(val.guid);
                                    }}
                                    onContextMenu={(e) => {
                                      let userDefined = val.ownertype === 2;
                                      //  changeSelectedSectionObj(val);

                                      e.preventDefault(); // Prevent default right-click behavior
                                      handleContextClick(val.guid, val.name, userDefined, index, e, family);
                                    }}
                                  >
                                    <span>
                                      {" "}
                                      {userDefined && String.fromCharCode(9670)} {val.name}
                                    </span>
                                  </li>
                                );
                              })}
                          </ul>
                        </Col>
                      </Row>
                    </Col>

                    <Col>
                      <Row style={{ marginTop: 10 }}>
                        <Col className="ps-0 px-0">
                          <div className="sx-list-header">{t("material_.label")}</div>
                        </Col>
                      </Row>
                      <Row className="table-body align-items-center">
                        <Col className="sx-list px-0">
                          <ul
                            className="sx-list-box sx-list-box--last"
                            style={{ height: sectionListMaxHeight, overflow: "auto" }}
                          >
                            {currentMaterials &&
                              currentMaterials.map((mat, index: number) => {
                                return (
                                  <li
                                    onClick={() => {
                                      setSelectedMaterial(mat.guid ?? null);
                                    }}
                                    className={`sx-list-item ${mat.name?.replace("/", "")} ${mat.guid === selectedMaterial ? "active" : ""}`}
                                    key={index}
                                  >
                                    {mat.name}
                                  </li>
                                );
                              })}
                          </ul>
                        </Col>
                      </Row>
                      <SectionCreationModal
                        type={family ?? ""}
                        SectionGuid={selectedSection ?? ""}
                        isCreationModalOpen={isCreationModalOpen}
                        modelState={modalState}
                        group={selectedGroup}
                        currentSelectedSectionGuid={
                          project?.data?.data?.input?.structures[0]?.elements[0].beam?.segments[0].layer?.start?.secGuid
                        }
                      />
                    </Col>
                  </Row>
                </Container>
                <Container className="ps-0 px-0" style={{ paddingBottom: 10 }}>
                  <MaterialSettings selectedGroup={currentProjectMaterial} />
                </Container>
              </Tab>
              <Tab eventKey="Details" title={t("details_.label")}>
                <MaterialSectionDetail />
              </Tab>
            </Tabs>
          </Container>
        </Col>
      </Row>
      <ContextMenu
        show={showContextMenu}
        setShow={setShowContextMenu}
        position={contextMenuPosition}
        options={contextMenuOptions}
      />
      <PopUpModal
        show={deletePopup.show}
        onClose={(ok) => {
          if (ok) {
            if (deletePopup.sectionGuid) {
              deleteSection(deletePopup.sectionGuid);
            }
          }

          setDeletePopup({ show: false, sectionGuid: null });
        }}
        question={"MSG_delete_section_"}
      />
    </Container>
  );
};
